import type { ResumeEngagement } from "../ResumeEngagementDataBuilder";

import {
  type CanonicalEnvironment,
  isProductionEnvironment,
} from "../../../../../../../shared/environment";

import { Platform } from "react-native";

const REZCOW_PROXY_PATH = "/m/api/rezcow/proxy";
const QA_PROXY_BASE_URL = "https://www.qa.indeed.net";

/**
 * Reads the INDEED_CSRF_TOKEN cookie value from the browser.
 * Indeed's site-wide CSRF protection requires this token as a query param on POST requests.
 */
function getIndeedCsrfToken(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(/(?:^|;\s*)INDEED_CSRF_TOKEN=([^;]*)/);
  return match?.[1] || undefined;
}

function getProxyBaseUrl(buildEnvironment?: CanonicalEnvironment): string {
  if (!buildEnvironment || isProductionEnvironment(buildEnvironment)) {
    return "";
  }
  return QA_PROXY_BASE_URL;
}

/**
 * Parameters for making an employer outreach API call
 */
export interface RezContactApiParams {
  /** The base URL for the rezcow service */
  rezcowBaseUrl: string;
  /** The API endpoint path (e.g., '/api/v1/contacted/decline', '/api/v1/contacted/interested') */
  endpoint: string;
  /** The engagement data from which to extract request parameters */
  engagement: ResumeEngagement;
  /** Additional payload fields to include in the request body */
  additionalPayload?: Record<string, unknown>;
  /** The source of the request */
  source?: string;
  /** The build environment. Used on web to resolve the proxy base URL. */
  buildEnvironment?: CanonicalEnvironment;
}

/**
 * Result of a successful employer outreach API call
 */
export interface RezContactApiResult {
  /** Whether the API call was successful */
  success: boolean;
  /** The service result status code */
  statusCode?: string;
  /** The full response data */
  data?: {
    serviceResult?: { statusCode: string };
    [key: string]: unknown;
  };
}

/**
 * Extracts base parameters from engagement data
 */
function buildBaseParams(engagement: ResumeEngagement, source: string): URLSearchParams {
  return new URLSearchParams({
    rAKey: engagement.recipient.id,
    sAKey: engagement.sender.id,
    uid: engagement.id,
    h: engagement.conversation.key,
    ecmi: engagement.conversation.initialMessage.id,
    source: source,
  });
}

/**
 * Builds the request payload from engagement data and additional fields
 */
function buildPayload(
  engagement: ResumeEngagement,
  additionalPayload?: Record<string, unknown>,
  source?: string,
): Record<string, unknown> {
  return {
    rAKey: engagement.recipient.id,
    sAKey: engagement.sender.id,
    uid: engagement.id,
    h: engagement.conversation.key,
    ecmi: engagement.conversation.initialMessage.id,
    source: source,
    ...additionalPayload,
  };
}

/**
 * Performs an OPTIONS preflight request to obtain CSRF token
 * @returns The extracted CSRF token, or empty string if not found
 */
async function fetchCsrfToken(url: string, params: URLSearchParams): Promise<string> {
  const optionsResponse = await fetch(`${url}?${params.toString()}`, {
    method: "OPTIONS",
    credentials: "include",
  });

  const setCookieHeader = optionsResponse.headers.get("Set-Cookie");
  const csrfMatch = setCookieHeader?.match(/CSRF=([^;]+)/);
  return csrfMatch?.[1] ?? "";
}

/**
 * Web proxy implementation for POST requests.
 * Routes through the indeedmobile `/m/api/rezcow/proxy` backend proxy which
 * handles CSRF token fetching/injection and avoids browser CORS restrictions.
 */
async function callRezContactApiViaProxy({
  endpoint,
  engagement,
  additionalPayload,
  source = "HOMEPAGE",
  buildEnvironment,
}: RezContactApiParams): Promise<RezContactApiResult> {
  const queryParams: Record<string, string> = {
    path: endpoint,
    rAKey: engagement.recipient.id,
    sAKey: engagement.sender.id,
    uid: engagement.id,
    h: engagement.conversation.key,
    ecmi: engagement.conversation.initialMessage.id,
    source,
  };

  const csrfToken = getIndeedCsrfToken();
  if (csrfToken) {
    queryParams.indeedcsrftoken = csrfToken;
  }

  if (additionalPayload) {
    for (const [key, value] of Object.entries(additionalPayload)) {
      if (value !== null && value !== undefined) {
        queryParams[key] = String(value);
      }
    }
  }

  const payload: Record<string, unknown> = {
    rAKey: engagement.recipient.id,
    sAKey: engagement.sender.id,
    uid: engagement.id,
    h: engagement.conversation.key,
    ecmi: engagement.conversation.initialMessage.id,
    source,
    ...additionalPayload,
  };

  const proxyBaseUrl = getProxyBaseUrl(buildEnvironment);
  const fullUrl = `${proxyBaseUrl}${REZCOW_PROXY_PATH}?${new URLSearchParams(queryParams).toString()}`;
  const response = await fetch(fullUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to call ${endpoint}: ${response.status}`);
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const data = (await response.json()) as {
    serviceResult?: { statusCode: string };
    [key: string]: unknown;
  };
  const success = data.serviceResult?.statusCode === "OK";

  return { success, statusCode: data.serviceResult?.statusCode, data };
}

/**
 * Makes an API call to rezcontact data service with CSRF token handling
 *
 * This utility handles the common pattern for employer outreach endpoints:
 * 1. Extract engagement parameters
 * 2. Make OPTIONS preflight request to get CSRF token
 * 3. Extract CSRF token from Set-Cookie header
 * 4. Make POST request with CSRF token in params and payload in body
 *
 * On web, requests are routed through the indeedmobile proxy to avoid CORS.
 *
 * @param params - The parameters for the API call
 * @returns A promise that resolves to the API result
 * @throws Error if the API call fails
 *
 * @example
 * ```typescript
 * const result = await callEmployerOutreachApi({
 *   rezcowBaseUrl: 'https://api.example.com',
 *   endpoint: '/api/v1/contacted/decline',
 *   engagement: engagements[0],
 * });
 *
 * if (result.success) {
 *   console.log('Success!');
 * }
 * ```
 *
 * @example
 * ```typescript
 * // With additional payload fields
 * const result = await callEmployerOutreachApi({
 *   rezcowBaseUrl: 'https://api.example.com',
 *   endpoint: '/api/v1/contacted/decline/survey',
 *   engagement: engagements[0],
 *   additionalPayload: {
 *     reason: 'not_interested',
 *     comment: 'Looking for remote work',
 *   },
 * });
 * ```
 */
export async function callRezContactApi({
  rezcowBaseUrl,
  endpoint,
  engagement,
  additionalPayload,
  source = "HOMEPAGE",
  buildEnvironment,
}: RezContactApiParams): Promise<RezContactApiResult> {
  if (Platform.OS === "web") {
    return callRezContactApiViaProxy({
      rezcowBaseUrl,
      endpoint,
      engagement,
      additionalPayload,
      source,
      buildEnvironment,
    });
  }

  const url = `${rezcowBaseUrl}${endpoint}`;
  const baseParams = buildBaseParams(engagement, source);

  // Make OPTIONS preflight request to get CSRF token
  const csrfToken = await fetchCsrfToken(url, baseParams);

  // Add CSRF token to params if found
  const params = new URLSearchParams(baseParams);
  if (csrfToken) {
    params.set("indeedcsrftoken", csrfToken);
  }

  // Build the payload
  const payload = buildPayload(engagement, additionalPayload, source);

  // Make the POST request
  const response = await fetch(`${url}?${params.toString()}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to call ${endpoint}: ${response.status}`);
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const data = (await response.json()) as {
    serviceResult?: { statusCode: string };
    [key: string]: unknown;
  };

  const success = data.serviceResult?.statusCode === "OK";

  return {
    success,
    statusCode: data.serviceResult?.statusCode,
    data,
  };
}

/**
 * Parameters for making a GET API call to rezcontact service
 */
export interface RezContactGetApiParams {
  /** The base URL for the service */
  baseUrl: string;
  /** The API endpoint path (e.g., '/api/v1/contacted/decline/reasons', '/contacted/apply/redirect') */
  endpoint: string;
  /** URL search params to include in the request */
  params: URLSearchParams;
  /** Whether to follow redirects. Defaults to true. Set to false to prevent 301/302 redirects. */
  followRedirect?: boolean;
  /** Whether to use json as Content-Type. Defaults to true. Set to false for default */
  useJsonContentType?: boolean;
  /** The build environment. Used on web to resolve the proxy base URL. */
  buildEnvironment?: CanonicalEnvironment;
}

/**
 * Result of a GET API call to rezcontact service
 */
export interface RezContactGetApiResult<T = unknown> {
  /** Whether the API call was successful */
  success: boolean;
  /** The service result status code */
  statusCode?: string;
  /** The full response data */
  data?: T;
}

/**
 * Web proxy implementation for GET requests.
 * Routes through the indeedmobile `/m/api/rezcow/proxy` backend proxy which
 * handles routing (rezcow vs ROZ) based on the `path` param and avoids CORS.
 */
async function callRezContactGetApiViaProxy<T = unknown>({
  endpoint,
  params,
  buildEnvironment,
}: RezContactGetApiParams): Promise<RezContactGetApiResult<T>> {
  const proxyParams = new URLSearchParams(params);
  proxyParams.set("path", endpoint);

  const csrfToken = getIndeedCsrfToken();
  if (csrfToken) {
    proxyParams.set("indeedcsrftoken", csrfToken);
  }

  const proxyBaseUrl = getProxyBaseUrl(buildEnvironment);
  const fullUrl = `${proxyBaseUrl}${REZCOW_PROXY_PATH}?${proxyParams.toString()}`;
  const response = await fetch(fullUrl, {
    method: "GET",
    credentials: "same-origin",
  });

  if (!response.ok) {
    throw new Error(`Failed to call ${endpoint}: ${response.status}`);
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const data = (await response.json()) as T & {
    serviceResult?: { statusCode: string };
  };
  const success = data.serviceResult?.statusCode === "OK";

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  return { success, statusCode: data.serviceResult?.statusCode, data: data as T };
}

/**
 * Makes a GET API call to rezcontact data service
 *
 * This utility handles GET requests to employer outreach endpoints.
 * The caller is responsible for building the URLSearchParams with the required parameters.
 *
 * On web, requests are routed through the indeedmobile proxy to avoid CORS.
 *
 * @param params - The parameters for the API call
 * @returns A promise that resolves to the API result
 * @throws Error if the API call fails
 *
 * @example
 * ```typescript
 * const params = new URLSearchParams({
 *   rAKey: engagement.recipient.id,
 *   sAKey: engagement.sender.id,
 *   uid: engagement.id,
 *   h: engagement.conversation.key,
 *   ecmi: engagement.conversation.initialMessage.id,
 *   source: 'HOMEPAGE',
 * });
 *
 * const result = await callRezContactGetApi({
 *   baseUrl: 'https://api.example.com',
 *   endpoint: '/api/v1/contacted/decline/reasons',
 *   params,
 * });
 *
 * if (result.success) {
 *   console.log('Reasons:', result.data);
 * }
 * ```
 *
 * @example
 * ```typescript
 * // For apply redirect endpoint with job key
 * const params = new URLSearchParams({
 *   rAKey: engagement.recipient.id,
 *   sAKey: engagement.sender.id,
 *   uid: engagement.id,
 *   h: engagement.conversation.key,
 *   jk: jobKey,
 * });
 *
 * const result = await callRezContactGetApi({
 *   baseUrl: 'https://api.example.com',
 *   endpoint: '/contacted/apply/redirect',
 *   params,
 * });
 * ```
 */
export async function callRezContactGetApi<T = unknown>({
  baseUrl,
  endpoint,
  params,
  followRedirect = true,
  useJsonContentType = true,
  buildEnvironment,
}: RezContactGetApiParams): Promise<RezContactGetApiResult<T>> {
  if (Platform.OS === "web") {
    return callRezContactGetApiViaProxy<T>({ baseUrl, endpoint, params, buildEnvironment });
  }

  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(`${url}?${params.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": useJsonContentType ? "application/json" : "text/html",
    },
    credentials: "include",
    redirect: followRedirect ? "follow" : "manual",
  });

  // When followRedirect is false, we just want to fire the request without following redirects
  // Consider any response (including 3xx redirects or 200 with HTML) as success
  if (!followRedirect) {
    return {
      success: response.status < 400,
      statusCode: response.status >= 400 ? "ERROR" : "OK",
    };
  }

  if (!response.ok) {
    throw new Error(`Failed to call ${endpoint}: ${response.status}`);
  }

  // When not using JSON content type, don't try to parse response as JSON
  if (!useJsonContentType) {
    const textData = await response.text();
    return {
      success: true,
      statusCode: "OK",
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      data: textData as T,
    };
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const data = (await response.json()) as T & {
    serviceResult?: { statusCode: string };
  };

  const success = data.serviceResult?.statusCode === "OK";

  return {
    success,
    statusCode: data.serviceResult?.statusCode,
    data,
  };
}
